import { Auth } from "aws-amplify";
import { CognitoUser } from "amazon-cognito-identity-js";

import {
  IUserCredentials,
  IRegisterCredentials,
  CognitoErrorCodes,
  IVerifyCredentials,
  IUser,
  IChangePassword,
  IResetPassword,
} from "Auth/types";
import { IMessage, IStatus } from "shared/types";
import { copy } from "shared/assets";
import { User } from "shared/assets/icons";
import {
  SentryService,
  loggerService,
  LogRocketService,
} from "shared/services";
import { parseJWT } from "Auth/utils";

/*
  Authentication with Amazon Cognito is done by using the Amplify Framework.
  https://docs.amplify.aws/lib/auth/getting-started/q/platform/js
  - All authentication events are logged to the console for debugging
*/
export class AuthService {
  private static getSourceUrl = () => window.location.host;

  /*
    Private helper method to get authenticated user if there is one
  */
  private static getCurrentCognitoUser = (
    force?: boolean
  ): Promise<CognitoUser> =>
    new Promise(async (resolve, reject) => {
      try {
        // get authenticated user from cache/localStorage
        // we don't want to bypass cache on every page load
        // we use bypass cache only after user/email edit
        let user = await Auth.currentAuthenticatedUser({
          bypassCache: force || false,
        });

        // if bypassCache: true, we don't need to compare the attributes
        if (force) {
          return resolve(user);
        }

        // get attributes from id token
        // this will be refreshed after 1 hour
        const token = await AuthService.getJWTToken();
        const tokenAttributes = parseJWT(token);

        // if cached attributes do not match with attributes in idToken
        // force refresh the user
        const compareAttributes = ["email", "family_name", "name"];
        const needsRefresh = compareAttributes.reduce((acc, attr) => {
          return acc || user.attributes[attr] !== tokenAttributes[attr];
        }, false);

        if (needsRefresh) {
          user = await Auth.currentAuthenticatedUser({
            bypassCache: true,
          });
        }

        resolve(user);
      } catch (error) {
        reject(error);
      }
    });

  /*
    Private helper method to create a user object of type IUser from a CognitoUser
    - using `any` type for cognitoUser because type definition file does not have the correct types
  */
  private static createUserFromCognitoUser = (cognitoUser: any): IUser => ({
    id: cognitoUser.attributes.sub,
    username: cognitoUser.username,
    attributes: {
      id: cognitoUser.attributes.sub,
      email: cognitoUser.attributes.email,
      firstname: cognitoUser.attributes.name,
      lastname: cognitoUser.attributes.family_name,
    },
  });

  /*
    Private helper method to resend account confirmation email
  */
  private static resendConfirmationEmail = async (email: string) => {
    try {
      // SourceUrl is passed as clientMetadata to ensure the link provided in the email has the correct domain
      await Auth.resendSignUp(email, {
        "custom:source_url": AuthService.getSourceUrl(),
      });
      loggerService.log({
        icon: "✅",
        title: "Resend confirmation email",
        body: "success",
      });
    } catch (error) {
      loggerService.log({
        icon: "❌",
        title: "Resend confirmation email",
        body: error,
      });
    }
  };

  /*
    Register a new account
    - if the email is already linked to an account the user gets an appropriate message.
  */
  static register = async ({
    email,
    firstname,
    lastname,
    password,
  }: IRegisterCredentials): Promise<IMessage> =>
    new Promise(async (resolve, reject) => {
      const cleanEmail = email.toLowerCase();

      try {
        await Auth.signUp({
          username: cleanEmail,
          password,
          attributes: {
            name: firstname,
            family_name: lastname,
            "custom:registration_url": AuthService.getSourceUrl(),
          },
          clientMetadata: {
            "custom:source_url": AuthService.getSourceUrl(),
          },
        });

        resolve({
          title: copy.register.success.title,
          body: copy.register.success.body.replace("%s", cleanEmail),
          status: IStatus.success,
          icon: User,
        });
      } catch (error) {
        loggerService.log({ icon: "❌", title: "Register", body: { error } });
        const message: IMessage = {
          status: IStatus.error,
          icon: User,
        };

        switch (error.code) {
          case CognitoErrorCodes.UsernameExistsException:
            message.body = copy.errors.usernameExists.message.replace(
              "%s",
              cleanEmail
            );
            break;
          default:
            message.body = copy.errors.general;
        }

        reject({ error, message });
      }
    });

  /*
    Verify a user account
    - if the verification code has expired a new email to verify the account is sent to the user.
  */
  static verifyAccount = async ({
    email,
    code,
  }: IVerifyCredentials): Promise<IMessage> =>
    new Promise(async (resolve, reject) => {
      try {
        await Auth.confirmSignUp(email, code);
        loggerService.log({
          icon: "✅",
          title: "Verify account",
          body: "success",
        });
        resolve({
          ...copy.verifyAccount.success,
          status: IStatus.success,
          icon: User,
        });
      } catch (error) {
        loggerService.log({
          icon: "❌",
          title: "Verify account",
          body: { error },
        });
        const message: IMessage = {
          status: IStatus.error,
          icon: User,
        };

        switch (error.code) {
          // verification code has expired
          case CognitoErrorCodes.ExpiredCodeException:
            AuthService.resendConfirmationEmail(email);
            message.body = copy.verifyAccount.linkExpired.body;
            message.title = copy.verifyAccount.linkExpired.title;
            break;
          // user account is already confirmed
          case CognitoErrorCodes.NotAuthorizedException:
            message.body = copy.verifyAccount.alreadyConfirmed.body;
            message.title = copy.verifyAccount.alreadyConfirmed.title;
            break;
          default:
            message.body = copy.errors.general;
        }

        reject({ error, message });
      }
    });

  /*
    Login
    - If the user tries to login with an account that has not been confirmed the account confirmation email is resend.
  */
  static login = async ({
    email,
    password,
  }: IUserCredentials): Promise<IUser> =>
    new Promise(async (resolve, reject) => {
      try {
        const cognitoUser = await Auth.signIn({
          username: email,
          password,
        });
        loggerService.log({
          icon: "✅",
          title: "Login",
          body: cognitoUser,
        });
        const user = AuthService.createUserFromCognitoUser(cognitoUser);

        // user is added to the sentry scope so errors can be logged when the user is authenticated
        SentryService.setUser(user);

        // user is added to the logrocket scope so errors are logged within the user context
        LogRocketService.setUser(user);

        resolve(user);
      } catch (error) {
        loggerService.log({ icon: "❌", title: "Login", body: error });
        const message: IMessage = {
          status: IStatus.error,
        };

        switch (error.code) {
          case CognitoErrorCodes.UserNotFoundException:
            message.body = copy.login.userNotFound;
            break;
          case CognitoErrorCodes.NotAuthorizedException:
            message.body = copy.login.wrongPassword;
            break;
          case CognitoErrorCodes.LimitExceededException:
            message.body = copy.login.limitExceeded;
            break;
          case CognitoErrorCodes.UserNotConfirmedException:
            AuthService.resendConfirmationEmail(email);
            message.body = copy.login.notConfirmed;
            break;
          default:
            message.body = copy.login.error;
        }

        reject({ error, message });
      }
    });

  /*
    Logout
  */
  static logout = async (global: boolean = false): Promise<void> =>
    new Promise(async (resolve, reject) => {
      try {
        await Auth.signOut({
          global,
        });

        loggerService.log({
          icon: "✅",
          title: "Logout",
          body: "success",
        });

        // user is removed from the sentry scope so errors are no longer logged when the user is not authenticated
        SentryService.clearUser();

        resolve();
      } catch (error) {
        loggerService.log({ icon: "❌", title: "Logout", body: error });
        reject({ error });
      }
    });

  /*
    Get authenticated user if there is one
    - users will remain authenticated as long as the token remains valid (even when the page is refreshed)
  */
  static getCurrentUser = async (force?: boolean): Promise<IUser> =>
    new Promise(async (resolve, reject) => {
      try {
        const cognitoUser = await AuthService.getCurrentCognitoUser(force);
        loggerService.log({
          icon: "✅",
          title: "Current user",
          body: cognitoUser,
        });
        const user = AuthService.createUserFromCognitoUser(cognitoUser);

        // user is added to the sentry scope so errors can be logged when the user is authenticated
        SentryService.setUser(user);

        // user is added to the logrocket scope so errors are logged within the user context
        LogRocketService.setUser(user);

        resolve(user);
      } catch (error) {
        reject({ error });
      }
    });

  static getJWTToken = async (): Promise<string> =>
    new Promise(async (resolve, reject) => {
      try {
        const currentUser = await Auth.currentSession();

        if (currentUser) {
          const idTokenExpire = currentUser.getIdToken().getExpiration();
          const refreshToken = currentUser.getRefreshToken();
          const currentTimeSeconds = Math.round(+new Date() / 1000);

          if (idTokenExpire < currentTimeSeconds) {
            Auth.currentAuthenticatedUser()
              .then((res) => {
                res.refreshSession(refreshToken, (err: any, data: any) => {
                  if (err) {
                    Auth.signOut();
                  } else {
                    console.log(
                      "Refreshed JWT token: ",
                      data.getIdToken().getJwtToken()
                    );
                    resolve(data.getIdToken().getJwtToken());
                  }
                });
              })
              .catch((err) => {
                reject(err);
              });
          } else {
            resolve(currentUser.getIdToken().getJwtToken());
          }
        }
      } catch (error) {
        reject(error);
      }
    });

  /*
    Change password of an authenticated user
  */
  static changePassword = async ({
    oldPassword,
    newPassword,
  }: IChangePassword): Promise<IMessage> =>
    new Promise(async (resolve, reject) => {
      try {
        const user = await AuthService.getCurrentCognitoUser();
        await Auth.changePassword(user, oldPassword || "", newPassword);
        loggerService.log({
          icon: "✅",
          title: "Change password",
          body: "success",
        });
        resolve({
          body: copy.changePassword.success,
          status: IStatus.success,
        });
      } catch (error) {
        loggerService.log({
          icon: "❌",
          title: "Change password",
          body: error,
        });
        const message: IMessage = {
          status: IStatus.error,
        };

        switch (error.code) {
          case CognitoErrorCodes.NotAuthorizedException:
          case CognitoErrorCodes.InvalidParameterException:
            message.body = copy.changePassword.failed;
            break;
          case CognitoErrorCodes.LimitExceededException:
            message.body = copy.errors.limitExceeded;
            break;
          default:
            message.body = copy.errors.general;
        }

        reject({ error, message });
      }
    });

  /*
    Request a password reset for a not authenticated user
  */
  static requestResetPassword = async (email: string): Promise<IMessage> =>
    new Promise(async (resolve, reject) => {
      try {
        // SourceUrl is passed as clientMetadata to ensure the link provided in the email has the correct domain
        await Auth.forgotPassword(email, {
          "custom:source_url": AuthService.getSourceUrl(),
        });
        loggerService.log({
          icon: "✅",
          title: "Request reset password",
          body: "Mail send",
        });
        resolve({
          body: copy.passwordReset.request.success,
          status: IStatus.success,
        });
      } catch (error) {
        loggerService.log({
          icon: "❌",
          title: "Forgot password",
          body: error,
        });
        const message: IMessage = {
          status: IStatus.error,
        };

        switch (error.code) {
          case CognitoErrorCodes.UserNotFoundException:
            message.body = copy.passwordReset.request.failed;
            break;
          case CognitoErrorCodes.LimitExceededException:
            message.body = copy.errors.limitExceeded;
            break;
          default:
            message.body = copy.errors.general;
        }

        reject({ error, message });
      }
    });

  /*
    Reset the password of a not authenticated user with a code received through a password reset request email
  */
  static resetPassword = async ({
    email,
    code,
    password,
  }: IResetPassword): Promise<IMessage> =>
    new Promise(async (resolve, reject) => {
      try {
        await Auth.forgotPasswordSubmit(email, code, password);
        loggerService.log({
          icon: "✅",
          title: "Reset password",
          body: "success",
        });
        resolve({
          body: copy.passwordReset.submit.success,
          status: IStatus.success,
        });
      } catch (error) {
        loggerService.log({
          icon: "❌",
          title: "Reset password",
          body: error,
        });
        const message: IMessage = {
          status: IStatus.error,
        };

        switch (error.code) {
          case CognitoErrorCodes.ExpiredCodeException:
            message.body = copy.passwordReset.linkExpired;
            break;
          default:
            message.body = copy.errors.general;
        }

        reject({ error, message });
      }
    });
}
